/* eslint-disable react/jsx-props-no-spreading, react/require-default-props */

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';

import {
  Accordion,
  Alert,
  Icons,
  Positions,
  Sizes,
  Tooltip,
} from '@sede-enterprise/shell-ds-react-framework';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import styled from 'styled-components';

import Button from '../../../../UI/Button';
import FormControlDatePicker from '../../../../UI/FormComponent/FormControlDatePicker';
import FormControlSelect from '../../../../UI/FormComponent/FormControlSelect';
import FormControlTextInput from '../../../../UI/FormComponent/FormControlTextInput';
import { Col, FormRow } from '../../../../UI/Grid';
import { Modal } from '../../../../UI/Modal';
import { sendNotification } from '../../../../UI/Notification';
import {
  ADD_UPDATE_TERMINAL_MAPPINGS,
  MESSAGES,
  DEAL_UNIT_ENUM,
} from '../../../../constants/FieldNominations';
import useGetProductionFields from '../../../../hooks/nominations/useGetProductionFields';
import useGetSourceProductionFields from '../../../../hooks/nominations/useGetSourceProductionFields';
import { getTerminalData, getSubTerminalData } from '../../../../store/reducers/Locations';
import { post } from '../../../../services/GenesisApi';
import { ITerminalMapping } from '../../../../types/FieldNominations.type';
import { ModalFooter } from '../style';
import dateFormat from '../../../../utils/dateFormat';
import { IFullState } from '../../../../types/All.type';
import { COMMON_MESSAGES } from '../../../../constants/Common';
import useSwingContracts from '../../../../hooks/fieldNomsSwingContract/useSwingContracts';
import type { AppDispatch } from '../../../../store';
import {
  ENERGY_TOKEN_REGEX,
  normalisePercentageExpression,
  validateFormulaExpression,
  VALID_FORMULA_CHARACTERS_REGEX,
} from '../../../../utils/fieldNomsMappings';
import { handleError, uniqueFieldArray } from '../../../../utils/common/utils';
import { FormulaTable } from './style';

const FormProviderWrapper = styled.div`
  .invalid {
    border: 1px solid rgba(217, 54, 56, 1);
  }

  .already-exists {
    color: rgba(217, 54, 56, 1);
    font-style: italic;
    text-align: center;
    font-size: 15px;
    font-weight: 500;
  }

  .shell-date-picker-time-panel-column:last-child {
    display: none;
  }
  .formula {
    font-size: 12px;
    font-weight: 500;
  }
`;

interface IAddEditTerminalMapping {
  open: boolean;
  onClose: () => void;
  selectedItem: ITerminalMapping | null;
  refreshData: () => void;
  checkExisting: (formData: ITerminalMapping) => {
    error: boolean;
    message: string;
  };
  sourceProductionFieldExists: (sourceProductionFieldId: number) => boolean;
}

const DD_MM_YYYY = 'DD MM YYYY';
const { yearMonthDay } = dateFormat;

const AddEditTerminalMapping: React.FC<IAddEditTerminalMapping> = ({
  open = false,
  onClose = () => {},
  selectedItem,
  refreshData = () => {},
  checkExisting,
  sourceProductionFieldExists,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const methods = useForm();
  const {
    reset,
    clearErrors,
    trigger,
    formState: { isDirty },
    watch,
  } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const [alreadyExists, setAlreadyExists] = useState<{
    error: boolean;
    message: string;
  }>({
    error: false,
    message: '',
  });

  const { loadingProductionFields, productionFields } = useGetProductionFields(open, true);
  const {
    isLoading,
    terminalData: { data: terminalData },
    subTerminalData: { data: subTerminalData },
  } = useSelector((state: IFullState) => state.Locations);

  const { isLoading: loadingCounterparties, counterpartyData } = useSelector(
    (state: IFullState) => state.Counterparty,
  );

  const { loadingSourceProductionFields, sourceProductionFields } = useGetSourceProductionFields();

  const { contracts: swingContractOptions, loading: swingLoading } = useSwingContracts();

  const {
    fields: formulaFields,
    append: appendFormula,
    remove: removeFormula,
  } = useFieldArray({
    control: methods.control,
    name: 'formula',
  });

  const timer = watch('timer');
  const validToDate = watch('validTo');
  const validFromDate = watch('validFrom');
  const daVarianceBreach = watch('daVarianceBreach');
  const wdVarianceBreach = watch('wdVarianceBreach');
  const sourceProductionFieldId = watch('sourceProductionFieldId');

  useEffect(() => {
    dispatch(getTerminalData());
    dispatch(getSubTerminalData());
  }, [dispatch]);

  useEffect(() => {
    if (validFromDate && validToDate && dayjs(validFromDate).isAfter(dayjs(validToDate))) {
      reset((prevValues) => ({
        ...prevValues,
        validFrom: validToDate,
        validTo: null,
      }));
    } else if (!validFromDate) {
      reset((prevValues) => ({
        ...prevValues,
        validTo: null,
      }));
    }
  }, [validFromDate, validToDate, reset]);

  useEffect(() => {
    if (selectedItem && open) {
      const { timer: selectedTimer, validFrom, validTo } = selectedItem;
      reset({
        deal_DealLegId: selectedItem.deal_DealLegId,
        dealUnit: selectedItem.dealUnit,
        sourceProductionFieldId: selectedItem.sourceProductionFieldId,
        productionFieldId: selectedItem.productionFieldId,
        terminalId: selectedItem.terminalId,
        subTerminalId: selectedItem.subTerminalId,
        counterpartyId: selectedItem.counterpartyId || null,
        formula: selectedItem.formula
          ? selectedItem.formula.map(
              ({ id, validFrom: fValidFrom, validTo: fValidTo, ...rest }) => ({
                ...rest,
                formulaId: id,
                validFrom: fValidFrom ? dayjs(fValidFrom) : null,
                validTo: fValidTo ? dayjs(fValidTo) : null,
              }),
            )
          : [],
        daVarianceBreach: selectedItem.daVarianceBreach,
        wdVarianceBreach: selectedItem.wdVarianceBreach,
        validFrom: validFrom ? dayjs(validFrom) : undefined,
        validTo: validTo ? dayjs(validTo) : undefined,
        timer: selectedTimer ? dayjs(`1/1/1 ${selectedTimer}`) : undefined,
        swingContractId: selectedItem?.swingContractId ?? null,
      });
    }
    if (!selectedItem && open) {
      reset({
        wdVarianceBreach: 10,
        daVarianceBreach: 25,
        swingContractId: null,
        dealUnit: 'Therms',
      });
    }
  }, [selectedItem, open]);

  const handleOnClose = () => {
    setAlreadyExists({
      error: false,
      message: '',
    });
    reset({
      deal_DealLegId: '',
      unit: null,
      sourceProductionFieldId: null,
      productionFieldId: null,
      terminalId: null,
      subTerminalId: null,
      timer: null,
      formula: [],
      daVarianceBreach: '',
      wdVarianceBreach: '',
      validFrom: null,
      validTo: null,
      autoUploadToleranceValue: '',
      autoUploadToleranceType: '',
      swingContractId: null,
    });
    onClose();
  };

  const handleOnSubmit = (formData) => {
    const { error, message } = checkExisting(formData);
    setAlreadyExists({
      error,
      message,
    });
    if (error) {
      return;
    }
    setLoading(true);
    let payload = formData;

    if (selectedItem) {
      payload = {
        ...payload,
        id: selectedItem?.id,
        validFrom: dayjs(formData.validFrom).format(yearMonthDay),
        validTo: dayjs(formData.validTo).format(yearMonthDay),
      };
    }

    const serialisedFormulas = (formData.formula ?? [])
      .filter((f) => !!(f.formula ?? '').trim())
      .map(({ formulaId, validFrom: fFrom, validTo: fTo, ...rest }) => ({
        ...rest,
        ...(formulaId ? { id: formulaId } : {}),
        validFrom: fFrom ? dayjs(fFrom).format(yearMonthDay) : null,
        validTo: fTo ? dayjs(fTo).format(yearMonthDay) : null,
      }));
    delete payload.formula;
    post(ADD_UPDATE_TERMINAL_MAPPINGS, {
      ...payload,
      formulas: serialisedFormulas,
      daVarianceBreach: formData.daVarianceBreach ?? null,
      wdVarianceBreach: formData.wdVarianceBreach ?? null,
      timer: formData.timer ? dayjs(formData.timer).format('HH:mm') : undefined,
      validFrom: dayjs(formData.validFrom).format(yearMonthDay),
      validTo: dayjs(formData.validTo).format(yearMonthDay),
      counterpartyId: formData.counterpartyId,
      swingContractId: formData.swingContractId ?? null,
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: `Mapping ${selectedItem ? 'Updated' : 'Created'} Successfully!`,
        });
        handleOnClose();
        refreshData();
      })
      .catch(() => {
        handleError(error, `Failed to ${selectedItem ? 'update' : 'create'} Mapping`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderFooter = () => (
    <ModalFooter>
      <Button
        onClick={methods.handleSubmit(handleOnSubmit)}
        loading={loading}
        disabled={loading || !isDirty}
      >
        {selectedItem ? 'Update' : 'Save'}
      </Button>
      <Button variant='outlined' onClick={handleOnClose}>
        Cancel
      </Button>
    </ModalFooter>
  );

  // add condition if atleast one formula is present
  const isValid = () =>
    timer ?? daVarianceBreach ?? wdVarianceBreach ?? methods.getValues('formula')?.length > 0;

  const validateFormula = (value: string, index: number): true | string => {
    const trimmed = (value ?? '').trim();
    const isExistingRow = !!(methods.getValues(`formula.${index}.formulaId`) as number | undefined);
    if (!trimmed) {
      return isExistingRow ? 'Formula is required for existing entries' : true;
    }

    // Must contain the word "energy" in any letter case, exactly once as a whole token.
    const energyMatches = trimmed.match(ENERGY_TOKEN_REGEX);
    if (!energyMatches) {
      return 'Formula must include the variable "energy"';
    }
    if (energyMatches.length > 1) {
      return 'Formula must contain "energy" only once';
    }

    if (/(^|[+(\-*/])\s*[+\-]\s*energy\b/i.test(trimmed)) {
      return 'Formula cannot use unary + or - directly before energy';
    }

    // Only allowed tokens: energy, numbers (incl. decimals), operators +−*/%, parentheses

    // Re-check properly: tokenise and allow only energy, numbers, operators, spaces
    const validTokenPattern = VALID_FORMULA_CHARACTERS_REGEX.test(
      trimmed.replace(ENERGY_TOKEN_REGEX, '0'),
    );
    if (!validTokenPattern) {
      return 'Formula contains invalid characters. Only "energy", numbers, +, -, *, /, % (percentage), () are allowed';
    }

    // Tokenise into: number/energy, operators, parens — then validate grammar
    // Replace energy with a placeholder number for eval-style token checking
    const baseExpression = trimmed.replace(ENERGY_TOKEN_REGEX, '1');
    const { expression: normalised, error: percentageError } =
      normalisePercentageExpression(baseExpression);
    if (percentageError) {
      return percentageError;
    }

    return validateFormulaExpression(normalised);
  };

  const validateFormulaValidFrom = (value: dayjs.Dayjs | null, index: number): true | string => {
    const formulaValue = methods.getValues(`formula.${index}.formula`) as string;
    if (!(formulaValue ?? '').trim()) {
      return true;
    }

    if (!value) {
      return 'Valid From is required when formula is entered';
    }

    const parentValidFrom = methods.getValues('validFrom');
    const parentValidTo = methods.getValues('validTo');
    if (parentValidFrom && dayjs(value).isBefore(dayjs(parentValidFrom), 'day')) {
      return 'Formula Valid From should be within Mapping Valid From/Valid To';
    }
    if (parentValidTo && dayjs(value).isAfter(dayjs(parentValidTo), 'day')) {
      return 'Formula Valid From should be within Mapping Valid From/Valid To';
    }

    const currentValidTo = methods.getValues(`formula.${index}.validTo`);
    if (currentValidTo && !dayjs(currentValidTo).isAfter(dayjs(value), 'day')) {
      return 'Valid To should be greater than Valid From';
    }

    const formulas = (methods.getValues('formula') ?? []) as Array<{
      formula?: string;
      validFrom?: dayjs.Dayjs | null;
      validTo?: dayjs.Dayjs | null;
    }>;
    if (currentValidTo) {
      const hasOverlap = formulas.some((row, rowIndex) => {
        if (rowIndex === index) {
          return false;
        }
        if (!(row?.formula ?? '').trim()) {
          return false;
        }
        if (!row?.validFrom || !row?.validTo) {
          return false;
        }

        const otherFrom = dayjs(row.validFrom);
        const otherTo = dayjs(row.validTo);
        const currentFrom = dayjs(value);
        const currentTo = dayjs(currentValidTo);

        return !currentTo.isBefore(otherFrom, 'day') && !currentFrom.isAfter(otherTo, 'day');
      });

      if (hasOverlap) {
        return 'Formula date range overlaps with another formula';
      }
    }

    return true;
  };

  const validateFormulaValidTo = (value: dayjs.Dayjs | null, index: number): true | string => {
    const formulaValue = methods.getValues(`formula.${index}.formula`) as string;
    if (!(formulaValue ?? '').trim()) {
      return true;
    }

    const validFrom = methods.getValues(`formula.${index}.validFrom`);

    if (!value) {
      return 'Valid To is required when formula is entered';
    }

    if (
      validFrom &&
      (dayjs(value).isSame(dayjs(validFrom), 'day') ||
        dayjs(value).isBefore(dayjs(validFrom), 'day'))
    ) {
      return 'Valid To should be greater than Valid From';
    }

    const parentValidFrom = methods.getValues('validFrom');
    const parentValidTo = methods.getValues('validTo');
    if (parentValidFrom && dayjs(value).isBefore(dayjs(parentValidFrom), 'day')) {
      return 'Formula Valid To should be within Mapping Valid From/Valid To';
    }
    if (parentValidTo && dayjs(value).isAfter(dayjs(parentValidTo), 'day')) {
      return 'Formula Valid To should be within Mapping Valid From/Valid To';
    }

    const formulas = (methods.getValues('formula') ?? []) as Array<{
      formula?: string;
      validFrom?: dayjs.Dayjs | null;
      validTo?: dayjs.Dayjs | null;
    }>;
    if (validFrom) {
      const hasOverlap = formulas.some((row, rowIndex) => {
        if (rowIndex === index) {
          return false;
        }
        if (!(row?.formula ?? '').trim()) {
          return false;
        }
        if (!row?.validFrom || !row?.validTo) {
          return false;
        }

        const otherFrom = dayjs(row.validFrom);
        const otherTo = dayjs(row.validTo);
        const currentFrom = dayjs(validFrom);
        const currentTo = dayjs(value);

        return !currentTo.isBefore(otherFrom, 'day') && !currentFrom.isAfter(otherTo, 'day');
      });

      if (hasOverlap) {
        return 'Formula date range overlaps with another formula';
      }
    }

    return true;
  };

  const triggerFormulaDateValidation = () => {
    const formulas = (methods.getValues('formula') ?? []) as Array<unknown>;
    if (!formulas.length) {
      return;
    }

    const fields = formulas.flatMap((_, index) => [
      `formula.${index}.validFrom`,
      `formula.${index}.validTo`,
    ]);
    trigger(fields);
  };

  const handleFormulaChange = (index: number, value: string) => {
    const isExistingRow = !!(methods.getValues(`formula.${index}.formulaId`) as number | undefined);
    if (!isExistingRow && !(value ?? '').trim()) {
      clearErrors([
        `formula.${index}.formula`,
        `formula.${index}.validFrom`,
        `formula.${index}.validTo`,
      ]);
    }

    triggerFormulaDateValidation();
  };

  const renderFormulaTooltipOverlay = () => (
    <div style={{ width: '520px', padding: '12px 16px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', gap: '24px' }}>
        <div style={{ flex: 1 }}>
          <strong>Formula rules:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: '16px' }}>
            <li>All formulas must be written considering KWh as the base unit of energy.</li>
            <li>
              Must contain the variable <code>energy</code> exactly once
            </li>
            <li>
              <code>energy</code> is the only allowed variable — no other names (e.g.{' '}
              <code>price</code>, <code>x</code>) are permitted
            </li>
            <li>
              Allowed operators: <code>+ - * / %</code> and parentheses
            </li>
            <li>
              <code>%</code> is a percentage of the preceding value (e.g. <code>energy * 10%</code>{' '}
              = energy × 0.1)
            </li>
            <li>
              Unary <code>+</code> or <code>-</code> before <code>energy</code> is not allowed
            </li>
          </ul>
        </div>
        <div style={{ flex: 1 }}>
          <div>
            <strong>Valid examples:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: '16px' }}>
              <li>
                <code>energy * 1.05</code>
              </li>
              <li>
                <code>(energy + 500) / 2</code>
              </li>
              <li>
                <code>energy * 10%</code>
              </li>
              <li>
                <code>energy / 1000 + 50</code>
              </li>
            </ul>
          </div>
          <div style={{ marginTop: '8px' }}>
            <strong>Invalid examples:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: '16px' }}>
              <li>
                <code>energy + energy</code> — used more than once
              </li>
              <li>
                <code>-energy</code> — unary minus before energy
              </li>
              <li>
                <code>energy ^ 2</code> — unsupported operator
              </li>
              <li>
                <code>price * 100</code> — missing energy variable
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFormulas = () => {
    return (
      <div>
        <FormulaTable>
          <thead>
            <tr>
              <th>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  Formula (KWh)
                  <Tooltip
                    triggerAction='hover'
                    overlay={renderFormulaTooltipOverlay()}
                    trigger={
                      <span
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                        data-testid='formula-info-icon'
                      >
                        <Icons.InfoCircle />
                      </span>
                    }
                  />
                </span>
              </th>
              <th>Valid From</th>
              <th>Valid To</th>
            </tr>
          </thead>
          <tbody>
            {formulaFields.map((field, index) => (
              <tr key={field.id}>
                <td>
                  <FormControlTextInput
                    controllerProps={{
                      name: `formula.${index}.formula`,
                      rules: {
                        validate: (value) => validateFormula(value, index),
                      },
                    }}
                    label=''
                    methods={methods}
                    handleOnChange={(event) => handleFormulaChange(index, event.target.value)}
                    data-testid={`formula-${index}`}
                  />
                </td>
                <td>
                  <FormControlDatePicker
                    controllerProps={{
                      name: `formula.${index}.validFrom`,
                      rules: {
                        validate: (value) => validateFormulaValidFrom(value, index),
                      },
                    }}
                    label=''
                    methods={methods}
                    data-testid={`formula-validFrom-${index}`}
                    handleOnChange={() => triggerFormulaDateValidation()}
                    inputProps={{ placeholder: DD_MM_YYYY }}
                  />
                </td>
                <td>
                  <FormControlDatePicker
                    controllerProps={{
                      name: `formula.${index}.validTo`,
                      rules: {
                        validate: (value) => validateFormulaValidTo(value, index),
                      },
                    }}
                    label=''
                    methods={methods}
                    data-testid={`formula-validTo-${index}`}
                    handleOnChange={() => triggerFormulaDateValidation()}
                    inputProps={{ placeholder: DD_MM_YYYY }}
                  />
                </td>
                {!(formulaFields[index] as any)?.formulaId && (
                  <td>
                    <Button
                      iconOnly
                      icon={<Icons.Trash />}
                      size={Sizes.Small}
                      title='Delete'
                      variant='transparent'
                      onClick={() => removeFormula(index)}
                      data-testid={`remove-formula-${index}`}
                    />
                  </td>
                )}
              </tr>
            ))}
            {formulaFields.length === 0 && (
              <tr>
                <td
                  colSpan={3}
                  style={{ textAlign: 'center', padding: '16px' }}
                  className='no-data-cell'
                >
                  <div className='title_wrapper'>No formulas added.</div>
                </td>
              </tr>
            )}
          </tbody>
        </FormulaTable>

        <Button
          onClick={() => appendFormula({ formula: '', validFrom: null, validTo: null })}
          data-testid='add-formula'
          icon={<Icons.Add />}
          size={Sizes.Small}
          variant='transparent'
          iconPosition={Positions.Left}
        >
          Add Formula
        </Button>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleOnClose}
      title={`${selectedItem ? 'Edit' : 'Add New'} Mapping`}
      isDraggable
      maskClosable={false}
      footer={renderFooter()}
      width='50%'
    >
      <FormProviderWrapper>
        <FormProvider {...methods}>
          <FormRow>
            <Col size={6}>
              <FormControlTextInput
                controllerProps={{
                  name: 'deal_DealLegId',
                  rules: {
                    required: MESSAGES.REQUIRED_DEAL_LEG_ID,
                    maxLength: {
                      value: 50,
                      message: COMMON_MESSAGES.MAX_LENGTH_50,
                    },
                    pattern: {
                      value: /^\d+_\d+$/,
                      message: MESSAGES.INVALID_DEAL_LEG_ID,
                    },
                  },
                }}
                label='Deal ID'
                data-testid='deal_DealLegId'
                methods={methods}
              />
            </Col>
            <Col size={6}>
              <FormControlSelect
                controllerProps={{
                  name: 'dealUnit',
                  rules: {
                    required: MESSAGES.REQUIRED_DEAL_UNIT,
                  },
                }}
                label='Deal Unit'
                isLoading={false}
                data={DEAL_UNIT_ENUM}
                methods={methods}
                data-testid='dealUnit'
                selectProps={{ allowClear: false }}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col size={6}>
              <FormControlSelect
                controllerProps={{
                  name: 'counterpartyId',
                  rules: {
                    required: MESSAGES.REQUIRED_COUNTERPARTY,
                  },
                }}
                label='Counterparty'
                isLoading={loadingCounterparties}
                data={counterpartyData.map((_) => ({
                  id: _.id,
                  name: _.counterpartyName,
                }))}
                methods={methods}
                data-testid='counterpartyId'
              />
            </Col>
            <Col size={6}>
              <FormControlSelect
                controllerProps={{ name: 'swingContractId' }}
                label='Swing Contract'
                isLoading={swingLoading}
                data={swingContractOptions}
                methods={methods}
                data-testid='swingContractId'
                selectProps={{ allowClear: true }}
              />
            </Col>
          </FormRow>

          <FormRow>
            <Col size={6}>
              <FormControlSelect
                controllerProps={{
                  name: 'sourceProductionFieldId',
                  rules: {
                    required: MESSAGES.REQUIRED_SOURCE_PRODUCTION_FIELD,
                  },
                }}
                label='Source Production Field'
                isLoading={loadingSourceProductionFields}
                data={uniqueFieldArray(sourceProductionFields)}
                methods={methods}
                data-testid='sourceProductionFieldId'
              />
            </Col>
            <Col size={6}>
              <FormControlDatePicker
                controllerProps={{
                  name: 'timer',
                }}
                label='Deadline'
                methods={methods}
                data-testid='timer'
                inputProps={{
                  picker: 'time',
                  placeholder: 'HH:mm',
                  format: 'HH:mm',
                }}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col size={6}>
              <FormControlTextInput
                controllerProps={{
                  name: 'wdVarianceBreach',
                  rules: {
                    min: { value: 0, message: MESSAGES.WD_DA_VARIANCE_RANGE },
                    max: { value: 100, message: MESSAGES.WD_DA_VARIANCE_RANGE },
                  },
                }}
                label='WD Variance Breach (%)'
                data-testid='wdVarianceBreach'
                methods={methods}
                inputProps={{
                  type: 'number',
                }}
              />
            </Col>
            <Col size={6}>
              <FormControlTextInput
                controllerProps={{
                  name: 'daVarianceBreach',
                  rules: {
                    min: { value: 0, message: MESSAGES.WD_DA_VARIANCE_RANGE },
                    max: { value: 100, message: MESSAGES.WD_DA_VARIANCE_RANGE },
                  },
                }}
                label='DA Variance Breach (%)'
                data-testid='daVarianceBreach'
                methods={methods}
                inputProps={{
                  type: 'number',
                }}
              />
            </Col>
          </FormRow>

          <FormRow>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'productionFieldId',
                  rules: {
                    required: MESSAGES.REQUIRED_PRODUCTION_FIELD,
                  },
                }}
                label='Production Field'
                isLoading={loadingProductionFields}
                data={productionFields}
                methods={methods}
                data-testid='productionField'
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'terminalId',
                  rules: {
                    required: MESSAGES.REQUIRED_TERMINAL,
                  },
                }}
                label='Terminal'
                isLoading={isLoading}
                data={terminalData
                  .filter((t) => t.isActive)
                  .map((_) => ({
                    id: _.id,
                    name: _.terminalName,
                  }))}
                methods={methods}
                data-testid='terminalId'
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'subTerminalId',
                  rules: {
                    required: MESSAGES.REQUIRED_SUB_TERMINAL,
                  },
                }}
                label='Sub Terminal'
                isLoading={isLoading}
                data={subTerminalData
                  .filter((st) => st.subTerminalIsActive)
                  .filter((_) => !!_.subTerminalName)
                  .map((_) => ({
                    id: _.subTerminalId,
                    name: _.subTerminalName,
                  }))}
                methods={methods}
                data-testid='subTerminalId'
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col size={6}>
              <FormControlDatePicker
                controllerProps={{
                  name: 'validFrom',
                  rules: {
                    required: 'Valid From Date is required',
                  },
                }}
                label='Valid From'
                methods={methods}
                data-testid='validFrom'
                inputProps={{
                  placeholder: DD_MM_YYYY,
                }}
              />
            </Col>
            <Col size={6}>
              <FormControlDatePicker
                controllerProps={{
                  name: 'validTo',
                  rules: {
                    required: 'Valid To Date is required',
                  },
                }}
                label='Valid To'
                methods={methods}
                data-testid='validTo'
                inputProps={{
                  placeholder: DD_MM_YYYY,
                  disabled: !watch('validFrom'),
                  disabledDate: (current) => {
                    return current && dayjs(current).isBefore(watch('validFrom'), 'day');
                  },
                }}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Accordion
              size='medium'
              iconSet='chevron'
              defaultActiveKey={['1']}
              items={[
                {
                  key: '1',
                  label: selectedItem?.formula?.length ? 'Modify Formulas' : 'Add Formulas',
                  children: renderFormulas(),
                },
              ]}
            />
          </FormRow>
          <FormRow>
            <Col size={12}>
              {alreadyExists.error && (
                <div className='already-exists'>{`Mapping already exists! ${alreadyExists.message}`}</div>
              )}
              {sourceProductionFieldExists(sourceProductionFieldId) && isValid() ? (
                <Alert sentiment='information'>
                  {MESSAGES.MAPPING_OVERRIDE.replace(
                    'addorupdate',
                    selectedItem ? 'updating a' : 'adding a new',
                  )}
                </Alert>
              ) : null}
            </Col>
          </FormRow>
        </FormProvider>
      </FormProviderWrapper>
    </Modal>
  );
};

export default AddEditTerminalMapping;
